import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, Save, Wallet, User, ArrowRightLeft, Trash2, RotateCcw } from "lucide-react";
import { MonthlyBudgetEditor } from "../components/MonthlyBudgetEditor";
import { CategoryManager } from "../components/CategoryManager";

const currencies = [
  { code: "SAR", name: "ريال سعودي" },
  { code: "AED", name: "درهم إماراتي" },
  { code: "EGP", name: "جنيه مصري" },
  { code: "KWD", name: "دينار كويتي" },
  { code: "QAR", name: "ريال قطري" },
  { code: "BHD", name: "دينار بحريني" },
  { code: "OMR", name: "ريال عماني" },
  { code: "JOD", name: "دينار أردني" },
  { code: "USD", name: "دولار أمريكي" },
  { code: "EUR", name: "يورو" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [initialCarry, setInitialCarry] = useState(0);
  const [currency, setCurrency] = useState("SAR");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingCarry, setDeletingCarry] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [budgetRevision, setBudgetRevision] = useState(0);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setInitialCarry(r.data?.initial_carry_over ?? 0);
        setCurrency(r.data?.currency || "SAR");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (initialCarry === "" || isNaN(Number(initialCarry))) {
      toast.error("الرصيد المُرحَّل الابتدائي يجب أن يكون رقماً");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings", {
        initial_carry_over: Number(initialCarry),
        currency,
      });
      setBudgetRevision((value) => value + 1);
      toast.success("تم حفظ الإعدادات وإعادة احتساب الأرصدة");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const deleteInitialCarry = async () => {
    const confirmed = window.confirm(
      "هل أنت متأكد من حذف الرصيد السابق المُرحَّل؟ سيتم تصفيره وإعادة احتساب جميع الأرصدة، ولن تُحذف أي فاتورة."
    );
    if (!confirmed) return;

    setDeletingCarry(true);
    try {
      await api.delete("/settings/initial-carry-over");
      setInitialCarry(0);
      setBudgetRevision((value) => value + 1);
      toast.success("تم حذف الرصيد السابق المُرحَّل وإعادة احتساب الأرصدة");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر حذف الرصيد المُرحَّل");
    } finally {
      setDeletingCarry(false);
    }
  };

  const resetProgram = async () => {
    const confirmation = window.prompt(
      "تحذير: ستُحذف جميع الفواتير والصور والأرصدة والإعدادات نهائيًا، مع الاحتفاظ بحساب الدخول والاشتراك.\n\nللتأكيد اكتب كلمة: تهيئة"
    );

    if (confirmation === null) return;

    if (confirmation.trim() !== "تهيئة") {
      toast.error("لم تتم التهيئة: كلمة التأكيد غير صحيحة");
      return;
    }

    setResetting(true);
    try {
      const response = await api.post("/settings/reset-program", {
        confirmation: "تهيئة",
      });

      toast.success(
        `تمت تهيئة البرنامج وحذف ${response.data?.deleted_invoices ?? 0} فاتورة`
      );

      setInitialCarry(0);
      setCurrency("SAR");

      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذرت تهيئة البرنامج");
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 shimmer rounded-md w-1/2" />
        <div className="h-64 shimmer rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up max-w-3xl mx-auto" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#8A8D86] mb-2 font-readex">الإعدادات</div>
        <h1 className="font-readex font-semibold text-3xl sm:text-4xl text-[#1A1C19] leading-tight">إدارة حسابك</h1>
        <p className="text-[#5C605A] mt-2">حدّد مصروف كل شهر وسجّل الدعم الإضافي، وعدّل العملة والرصيد المرحّل.</p>
      </div>

      {/* User info */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-md bg-[#F5F4EF] flex items-center justify-center">
            <User className="w-5 h-5 text-[#1F3A33]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-readex font-medium text-[#1A1C19]">حسابك</div>
            <div className="text-xs text-[#8A8D86]">{user?.email}</div>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-1.5 border-b border-[#1A1C19]/5">
            <span className="text-[#5C605A]">الاسم</span>
            <span className="text-[#1A1C19]">{user?.name || "—"}</span>
          </div>
          <div className="flex justify-between py-1.5">
            <span className="text-[#5C605A]">البريد الإلكتروني</span>
            <span className="text-[#1A1C19]" dir="ltr">{user?.email}</span>
          </div>
        </div>
      </div>

      <MonthlyBudgetEditor key={budgetRevision} currency={currency} />

      {/* Finance settings */}
      <div className="bg-white border border-[#1A1C19]/10 rounded-md p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-md bg-[#F5F4EF] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-[#1F3A33]" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-readex font-medium text-[#1A1C19]">الرصيد والعملة</div>
            <div className="text-xs text-[#8A8D86]">تعديل الرصيد المرحّل الابتدائي يؤثر على سلسلة الترحيل</div>
          </div>
        </div>

        <div className="space-y-4">
          <label className="block">
            <span className="flex items-center gap-1.5 text-sm font-medium text-[#1A1C19] mb-1.5 font-readex">
              <ArrowRightLeft className="w-3.5 h-3.5 text-[#1F3A33]" strokeWidth={1.5} />
              رصيد مُرحَّل ابتدائي (مرة واحدة)
            </span>
            <input
              type="number"
              step="0.01"
              data-testid="initial-carry-input"
              value={initialCarry}
              onChange={(e) => setInitialCarry(e.target.value)}
              className="input font-mono-num"
              placeholder="0.00"
            />
            <span className="block text-xs text-[#8A8D86] mt-1.5 font-readex leading-relaxed">
              إذا كان لديك رصيد متبقٍ <strong>من قبل بدء استخدام التطبيق</strong>، أدخله هنا. سيُضاف لأول شهر فقط، ثم تُحسَب الأشهر التالية تلقائياً من إغلاق الشهر السابق. اتركه <span className="font-mono-num">0.00</span> إذا لم يكن لديك رصيد سابق.
            </span>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-[#1A1C19] mb-1.5 font-readex">العملة</span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="input bg-white"
              data-testid="currency-select"
            >
              {currencies.map((c) => (
                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
              ))}
            </select>
          </label>

          <button
            onClick={save}
            disabled={saving}
            data-testid="save-settings-btn"
            className="bg-[#1F3A33] text-white hover:bg-[#172A25] disabled:opacity-60 transition-colors rounded-md px-6 py-3 font-medium font-readex inline-flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <Save className="w-5 h-5" strokeWidth={1.5} />}
            حفظ التغييرات
          </button>

          <button
            type="button"
            onClick={deleteInitialCarry}
            disabled={deletingCarry || Number(initialCarry) === 0}
            data-testid="delete-initial-carry-btn"
            className="bg-white border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-md px-6 py-3 font-medium font-readex inline-flex items-center gap-2"
          >
            {deletingCarry
              ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
              : <Trash2 className="w-5 h-5" strokeWidth={1.5} />}
            حذف الرصيد السابق المُرحَّل
          </button>
        </div>
      </div>

      {/* Category management */}
      <CategoryManager />

      {/* Danger zone */}
      <div className="bg-red-50 border border-red-200 rounded-md p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-md bg-red-100 flex items-center justify-center">
            <RotateCcw className="w-5 h-5 text-red-700" strokeWidth={1.5} />
          </div>

          <div className="flex-1">
            <div className="font-readex font-semibold text-red-800">
              تهيئة البرنامج من جديد
            </div>

            <p className="text-sm text-red-700 mt-1 leading-relaxed">
              يحذف جميع الفواتير والصور والأرصدة والإعدادات والتصنيفات المخصصة.
              سيبقى حساب تسجيل الدخول والاشتراك دون حذف.
            </p>

            <button
              type="button"
              onClick={resetProgram}
              disabled={resetting}
              data-testid="reset-program-btn"
              className="mt-4 bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 transition-colors rounded-md px-6 py-3 font-medium font-readex inline-flex items-center gap-2"
            >
              {resetting
                ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
                : <RotateCcw className="w-5 h-5" strokeWidth={1.5} />}
              تهيئة البرنامج من جديد
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .input { width:100%; background:white; border:1px solid rgba(26,28,25,0.15); border-radius:.375rem; padding:.75rem 1rem; font-size:1rem; transition:all .2s; }
        .input:focus { outline:none; border-color:#1F3A33; box-shadow:0 0 0 3px rgba(31,58,51,0.12); }
      `}</style>
    </div>
  );
}

